<template>
    <IntegrationModal
        title="Constant Contact"
        documentation-url="https://support.topol.io/en/articles/6424392-export-to-constant-contact"
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createOAuthUrl"
        @edit="updateIntegration"
    >
        <template #inputs>
            <div class="mb-6 w-full">
                <TextInput
                    v-model="v$.name.$model"
                    class="mt-6"
                    placeholder="Enter your preferred integration name"
                    label="Name"
                    :error="getError('name')"
                />

                <TextInput
                    v-model="v$.from_name.$model"
                    class="mt-6 w-full"
                    placeholder="Your Constant Contact from name"
                    label="From name"
                    required
                    :error="getError('from_name')"
                />

                <TextInput
                    v-model="state.from_email"
                    class="mt-6 w-full"
                    placeholder="Your Constant Contact from email"
                    label="From email"
                    required
                    :error="getError('from_email')"
                    @blur="v$.from_email.$touch()"
                />

                <TextInput
                    v-model="state.reply_to_email"
                    class="mt-6 w-full"
                    placeholder="Your Constant Contact reply to email"
                    label="Reply to email"
                    required
                    :error="getError('reply_to_email')"
                    @blur="v$.reply_to_email.$touch()"
                />
            </div>
        </template>
    </IntegrationModal>
</template>
<script lang="ts" setup>
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { maxLength, required, email } from '@vuelidate/validators';
import IInputErrors from '@/types/Atomic/IInputErrors';
import { PropType } from 'vue';
import {
    IConstantContact,
    IIntegration,
} from '@/types/Integrations/IIntegration';
import { AxiosError } from 'axios';

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<IConstantContact>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(['close']);

const loading = ref(false);
const errors = ref<IInputErrors>({});

const state = reactive({
    name: props.integration?.name || '',
    from_name: props.integration?.data.from_name || '',
    from_email: props.integration?.data.from_email || '',
    reply_to_email: props.integration?.data.reply_to_email || '',
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
    from_name: {
        required,
        maxLength: maxLength(255),
    },
    from_email: {
        email,
        required,
        maxLength: maxLength(255),
    },
    reply_to_email: {
        email,
        required,
        maxLength: maxLength(255),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || '';
};

const createOAuthUrl = async () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        loading.value = true;
        await axios
            .get(
                route('integrations.constant-contact.o-auth-two-url', {
                    name: state.name,
                    from_name: state.from_name,
                    from_email: state.from_email,
                    reply_to_email: state.reply_to_email,
                })
            )
            .then((response) => {
                window.location.href = response.data.data;
                emit('close');
            })
            .catch((err) => {
                errors.value = err;

                create({
                    title: 'Integration not added',
                    text:
                        err instanceof AxiosError
                            ? err.response?.data?.message ??
                              'There was an error adding Constant Contact integration.'
                            : 'There was an error adding Constant Contact integration.',
                    type: 'error',
                });
            });
        loading.value = false;
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        return;
    } else {
        loading.value = true;
        router.put(
            route('integrations.constant-contact.update', {
                integration: props.integration.id,
            }),
            {
                name: state.name,
                from_name: state.from_name,
                from_email: state.from_email,
                reply_to_email: state.reply_to_email,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit('close');
                    create({
                        title: 'Integration updated',
                        type: 'success',
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not updated',
                        text:
                            err.message ??
                            'There was an error updating Constant Contact integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};
</script>

<style scoped></style>
